import React, { useEffect, useRef, useState, useMemo } from 'react';
import OrganizationSelector from './OrganizationSelector';
import { useFetchDivisions } from '../../hooks/api';
import { ui } from '../../config/uiSizes';

export type MigrationMode = 'orgToOrg' | 'divisionToDivision';

interface DivisionToOrgSelectorProps {
  sourceOrganizationKey: string;
  migrationMode: MigrationMode;
  onMigrationModeChange: (mode: MigrationMode) => void;
  // Org-to-Org fields
  targetOrganization: string;
  onTargetOrganizationChange: (org: string) => void;
  // D2D fields
  sourceDivisionId: string;
  sourceDivisionName: string;
  targetDivisionId: string;
  targetDivisionName: string;
  onSourceDivisionChange: (id: string, name: string) => void;
  onTargetDivisionChange: (id: string, name: string) => void;
}

const DivisionToOrgSelector: React.FC<DivisionToOrgSelectorProps> = ({
  sourceOrganizationKey: sourceOrganizationId,
  migrationMode,
  onMigrationModeChange,
  targetOrganization,
  onTargetOrganizationChange,
  sourceDivisionId,
  sourceDivisionName,
  targetDivisionId,
  targetDivisionName,
  onSourceDivisionChange,
  onTargetDivisionChange
}) => {
  const { divisions, loading: loadingDivisions, fetchDivisions } = useFetchDivisions(sourceOrganizationId);

  // Re-fetch when source org changes; clear division selections
  const prevOrgIdRef = useRef<string>('');
  useEffect(() => {
    if (sourceOrganizationId && sourceOrganizationId !== prevOrgIdRef.current) {
      if (migrationMode === 'divisionToDivision') {
        void fetchDivisions();
        onSourceDivisionChange('', '');
        onTargetDivisionChange('', '');
      }
      prevOrgIdRef.current = sourceOrganizationId;
    }
  }, [sourceOrganizationId, migrationMode, fetchDivisions, onSourceDivisionChange, onTargetDivisionChange]);

  // Fetch divisions when mode switches to D2D
  const prevModeRef = useRef<MigrationMode>('orgToOrg');
  useEffect(() => {
    if (migrationMode === 'divisionToDivision' && prevModeRef.current !== 'divisionToDivision') {
      if (sourceOrganizationId) {
        void fetchDivisions();
      }
    }
    prevModeRef.current = migrationMode;
  }, [migrationMode, sourceOrganizationId, fetchDivisions]);

  // Dropdown open/search state
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');

  const fromBtnRef = useRef<HTMLButtonElement>(null);
  const fromListRef = useRef<HTMLDivElement>(null);
  const toBtnRef = useRef<HTMLButtonElement>(null);
  const toListRef = useRef<HTMLDivElement>(null);

  // Close both dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        fromListRef.current && !fromListRef.current.contains(target) &&
        fromBtnRef.current && !fromBtnRef.current.contains(target)
      ) {
        setFromOpen(false);
      }
      if (
        toListRef.current && !toListRef.current.contains(target) &&
        toBtnRef.current && !toBtnRef.current.contains(target)
      ) {
        setToOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredFrom = useMemo(() => {
    if (!fromSearch.trim()) return divisions;
    const lower = fromSearch.toLowerCase();
    return divisions.filter((d) => d.name.toLowerCase().includes(lower));
  }, [divisions, fromSearch]);

  const filteredTo = useMemo(() => {
    if (!toSearch.trim()) return divisions;
    const lower = toSearch.toLowerCase();
    return divisions.filter((d) => d.name.toLowerCase().includes(lower));
  }, [divisions, toSearch]);

  const dropdownButtonClass = `w-full text-left ${ui.text.body} flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`;

  return (
    <div className="w-full space-y-4">
      {/* ── Migration type toggle ── */}
      <div>
        <p className={`${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100 mb-4`}>
          Migration Type:
        </p>
        <div className="space-y-4 pl-1">
          <label className={`flex items-start gap-3 cursor-pointer`}>
            <input
              type="radio"
              name="migrationMode"
              value="orgToOrg"
              checked={migrationMode === 'orgToOrg'}
              onChange={() => onMigrationModeChange('orgToOrg')}
              className="mt-1 w-4 h-4 accent-blue-600 shrink-0"
            />
            <div className="space-y-0.5">
              <span className={`${ui.text.label} font-semibold text-gray-900 dark:text-gray-100`}>
                Organization to Organization
              </span>
              <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                Copy resources from one Genesys Cloud organization to another.
              </p>
            </div>
          </label>

          <label className={`flex items-start gap-3 cursor-pointer`}>
            <input
              type="radio"
              name="migrationMode"
              value="divisionToDivision"
              checked={migrationMode === 'divisionToDivision'}
              onChange={() => onMigrationModeChange('divisionToDivision')}
              className="mt-1 w-4 h-4 accent-blue-600 shrink-0"
            />
            <div className="space-y-0.5 pb-2">
              <span className={`${ui.text.label} font-semibold text-gray-900 dark:text-gray-100`}>
                Division to Division
              </span>
              <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                Copy or reassign resources between divisions within the same Genesys Cloud organization.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* ── Conditional form area ── */}
      {migrationMode === 'orgToOrg' ? (
        <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
          <OrganizationSelector
            orgVariant="targetOrganization"
            value={targetOrganization}
            onChange={onTargetOrganizationChange}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 dark:border-blue-600 pl-3 space-y-4">

            {/* FROM dropdown */}
            <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
              <label
                className={`w-48 shrink-0 ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
              >
                Source:
              </label>
              <div className="flex-1 w-full relative">
                <button
                  type="button"
                  ref={fromBtnRef}
                  onClick={() => !loadingDivisions && setFromOpen((v) => !v)}
                  disabled={loadingDivisions || !sourceOrganizationId}
                  className={dropdownButtonClass}
                  aria-haspopup="listbox"
                  aria-expanded={fromOpen}
                >
                  <span className={`truncate ${!sourceDivisionId ? 'text-gray-500 dark:text-gray-400' : ''}`}>
                    {loadingDivisions
                      ? 'Loading divisions...'
                      : sourceDivisionName || (!sourceOrganizationId ? 'Select a source organization first' : 'Select a division')}
                  </span>
                  {loadingDivisions ? (
                    <svg className={`${ui.svg.sm} text-gray-400 shrink-0 animate-spin`} fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  ) : (
                    <svg className={`${ui.svg.sm} text-gray-400 shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>

                {fromOpen && (
                  <div
                    ref={fromListRef}
                    className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    role="listbox"
                  >
                    <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                      <input
                        type="text"
                        value={fromSearch}
                        onChange={(e) => setFromSearch(e.target.value)}
                        placeholder="Search divisions..."
                        className={`w-full ${ui.input.sm} border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        autoFocus
                      />
                    </div>
                    {filteredFrom.length === 0 ? (
                      <div className={`px-3 py-2 ${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                        No divisions found
                      </div>
                    ) : (
                      filteredFrom.map((division) => (
                        <button
                          key={division.id}
                          type="button"
                          role="option"
                          aria-selected={sourceDivisionId === division.id}
                          onClick={() => {
                            onSourceDivisionChange(division.id, division.name);
                            if (targetDivisionId === division.id) {
                              onTargetDivisionChange('', '');
                            }
                            setFromOpen(false);
                            setFromSearch('');
                          }}
                          className={`w-full text-left px-3 py-2 ${ui.text.body} transition-colors ${sourceDivisionId === division.id
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
                            }`}
                        >
                          {division.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* TO dropdown */}
            <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
              <label
                className={`w-48 shrink-0 ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
              >
                Target:
              </label>
              <div className="flex-1 w-full relative">
                <button
                  type="button"
                  ref={toBtnRef}
                  onClick={() => !loadingDivisions && setToOpen((v) => !v)}
                  disabled={loadingDivisions || !sourceOrganizationId || !sourceDivisionId}
                  className={dropdownButtonClass}
                  aria-haspopup="listbox"
                  aria-expanded={toOpen}
                >
                  <span className={`truncate ${!targetDivisionId ? 'text-gray-500 dark:text-gray-400' : ''}`}>
                    {loadingDivisions
                      ? 'Loading divisions...'
                      : targetDivisionName || (sourceDivisionId ? 'Select a target division' : 'Select a source division first')}
                  </span>
                  {loadingDivisions ? (
                    <svg className={`${ui.svg.sm} text-gray-400 shrink-0 animate-spin`} fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  ) : (
                    <svg className={`${ui.svg.sm} text-gray-400 shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>

                {toOpen && (
                  <div
                    ref={toListRef}
                    className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    role="listbox"
                  >
                    <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                      <input
                        type="text"
                        value={toSearch}
                        onChange={(e) => setToSearch(e.target.value)}
                        placeholder="Search divisions..."
                        className={`w-full ${ui.input.sm} border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        autoFocus
                      />
                    </div>
                    {filteredTo.length === 0 ? (
                      <div className={`px-3 py-2 ${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                        No divisions found
                      </div>
                    ) : (
                      filteredTo.map((division) => {
                        const isSameAsFrom = division.id === sourceDivisionId;
                        return (
                          <button
                            key={division.id}
                            type="button"
                            role="option"
                            aria-selected={targetDivisionId === division.id}
                            disabled={isSameAsFrom}
                            onClick={() => {
                              onTargetDivisionChange(division.id, division.name);
                              setToOpen(false);
                              setToSearch('');
                            }}
                            className={`w-full text-left px-3 py-2 ${ui.text.body} transition-colors ${isSameAsFrom
                              ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-600'
                              : targetDivisionId === division.id
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
                              }`}
                          >
                            {division.name}
                            {isSameAsFrom && (
                              <span className={`ml-2 ${ui.text.xs} text-gray-400`}>(source)</span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Non-dismissible D2D confirmation banner */}
          <div className="rounded-lg border-2 border-blue-500 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
            <p className={`${ui.text.label} font-semibold text-blue-800 dark:text-blue-300 text-center`}>
              You are performing a <span className="font-bold">Division-to-Division</span> migration.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DivisionToOrgSelector;
